import { AddressType, BusinessType } from '@/@types/generated'
import InputBox from '@/components/Atoms/InputBox'
import { LockedField } from '@/components/Atoms/LockedField'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Header } from '@/components/ui/header'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select.tsx'
import { Separator } from '@/components/ui/separator'
import { provinces, states } from '@/data/states'
import { useAddressValidationDialog } from '@/hooks/useAddressValidationDialog'
import { useError } from '@/hooks/useError'
import { addressSchema, emailSchema, phoneSchema } from '@/lib/form'
import format from '@/lib/format'
import { zodResolver } from '@hookform/resolvers/zod'
import _ from 'lodash'
import { InfoIcon } from 'lucide-react'
import { PropsWithChildren, useEffect, useState } from 'react'
import { SubmitErrorHandler, SubmitHandler, useForm } from 'react-hook-form'
import { toast } from 'sonner'
import z from 'zod'

export interface BusinessEntityFormProps {
  initialValues?: Partial<TFormSchema>
  onSubmit: (data: TFormSchema) => Promise<void>
  children: (props: { isSubmitting: boolean }) => React.ReactNode

  // Field flags
  withBusinessType?: boolean
  withStateFileNumber?: boolean
  withTaxId?: boolean
  withOwnerInfo?: boolean

  // Locked (read-only) display when carrier identity is sourced from USDOT
  lockedByUsdot?: boolean
  lockedDisplayValues?: {
    legalName?: string | null
    dbaName?: string | null
    usdotNumber?: string | null
    physicalAddress?: string | null
    mailingAddress?: string | null
  }
}

export function BusinessEntityForm({
  initialValues,
  withBusinessType,
  withStateFileNumber,
  withTaxId,
  withOwnerInfo,
  onSubmit,
  children,
  lockedByUsdot = false,
  lockedDisplayValues,
}: BusinessEntityFormProps) {
  // Hooks
  const { showError } = useError()
  const { validateAndConfirm, dialog: addressValidationDialog } =
    useAddressValidationDialog()

  // State
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Form
  const form = useForm<TFormSchema>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      legalName: initialValues?.legalName ?? '',
      dbaName: initialValues?.dbaName ?? '',
      businessType: withBusinessType ? initialValues?.businessType : undefined,
      stateFileNumber: initialValues?.stateFileNumber,
      taxId: withTaxId ? (initialValues?.taxId ?? '') : undefined,
      physicalAddress: {
        street1: initialValues?.physicalAddress?.street1 ?? '',
        street2: initialValues?.physicalAddress?.street2 ?? '',
        city: initialValues?.physicalAddress?.city ?? '',
        stateProvince: initialValues?.physicalAddress?.stateProvince ?? '',
        postalCode: initialValues?.physicalAddress?.postalCode ?? '',
      },
      mailingIsBusiness:
        _.isEqual(
          initialValues?.physicalAddress,
          initialValues?.mailingAddress
        ) || !initialValues?.mailingAddress,
      mailingAddress: {
        street1: initialValues?.mailingAddress?.street1 ?? '',
        street2: initialValues?.mailingAddress?.street2 ?? '',
        city: initialValues?.mailingAddress?.city ?? '',
        stateProvince: initialValues?.mailingAddress?.stateProvince ?? '',
        postalCode: initialValues?.mailingAddress?.postalCode ?? '',
      },
      ownerInfo: withOwnerInfo
        ? {
            name: initialValues?.ownerInfo?.name ?? '',
            phoneNumber:
              initialValues?.ownerInfo?.phoneNumber.replace(/\D/g, '') ?? '', // strip non-numeric characters
            email: initialValues?.ownerInfo?.email ?? '',
          }
        : undefined,

      // Field flags
      withTaxId,
      withBusinessType,
    },
  })

  const handleSubmit: SubmitHandler<TFormSchema> = async (data) => {
    toast.dismiss(BusinessEntityFormErrorId)

    try {
      setIsSubmitting(true)

      let physicalAddress = data.physicalAddress
      let mailingAddress = data.mailingAddress

      // Skip address validation when the carrier identity is sourced from USDOT
      if (!lockedByUsdot) {
        const { address: confirmedPhysical } = await validateAndConfirm({
          type: AddressType.PHYSICAL,
          ...data.physicalAddress,
        })
        if (!confirmedPhysical) return
        physicalAddress = {
          ...data.physicalAddress,
          street1: confirmedPhysical.street1 ?? data.physicalAddress.street1,
          city: confirmedPhysical.city ?? data.physicalAddress.city,
          stateProvince:
            confirmedPhysical.stateProvince ??
            data.physicalAddress.stateProvince,
          postalCode:
            confirmedPhysical.postalCode ?? data.physicalAddress.postalCode,
        }

        if (!data.mailingIsBusiness) {
          const { address: confirmedMailing } = await validateAndConfirm({
            type: AddressType.MAILING,
            ...data.mailingAddress,
          })
          if (!confirmedMailing) return
          mailingAddress = {
            ...data.mailingAddress,
            street1: confirmedMailing.street1 ?? data.mailingAddress.street1,
            city: confirmedMailing.city ?? data.mailingAddress.city,
            stateProvince:
              confirmedMailing.stateProvince ??
              data.mailingAddress.stateProvince,
            postalCode:
              confirmedMailing.postalCode ?? data.mailingAddress.postalCode,
          }
        }
      }

      // If taxId is bullets, omit from data
      const taxId = data.taxId?.includes('•') ? undefined : data.taxId

      // Address passes validation, call parent submit
      await onSubmit({ ...data, taxId, physicalAddress, mailingAddress })
    } catch (error) {
      showError(error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleError: SubmitErrorHandler<TFormSchema> = (errors) => {
    showError(
      new Error('Please fix the errors in the form before submitting.'),
      'Form Submission Error',
      { id: BusinessEntityFormErrorId }
    )
    console.error('Form validation errors:', errors)
  }

  // Is USA
  const isUSA = (v: string) =>
    !v || v.length < 2 || !!states.find(({ code }) => code === v.toUpperCase())

  // Sync mailing address if same as business
  const [
    mailingIsBusiness,
    physicalAddressStreet1,
    physicalAddressStreet2,
    physicalAddressCity,
    physicalAddressState,
    physicalAddressPostalCode,
  ] = form.watch([
    'mailingIsBusiness',
    'physicalAddress.street1',
    'physicalAddress.street2',
    'physicalAddress.city',
    'physicalAddress.stateProvince',
    'physicalAddress.postalCode',
  ])
  useEffect(() => {
    if (!mailingIsBusiness) return

    const sv = form.setValue
    const opts = { shouldValidate: true }
    sv('mailingAddress.street1', physicalAddressStreet1, opts)
    sv('mailingAddress.street2', physicalAddressStreet2, opts)
    sv('mailingAddress.city', physicalAddressCity, opts)
    sv('mailingAddress.stateProvince', physicalAddressState, opts)
    sv('mailingAddress.postalCode', physicalAddressPostalCode, opts)
  }, [
    mailingIsBusiness,
    form,
    physicalAddressStreet1,
    physicalAddressStreet2,
    physicalAddressCity,
    physicalAddressState,
    physicalAddressPostalCode,
  ])

  return (
    <Form {...form}>
      <Alert variant="info">
        <InfoIcon />
        <AlertDescription className="inline">
          <strong className="font-bold">NOTE:</strong> All fields marked with an
          asterisk (<span className="text-destructive px-0.5">*</span>) are
          required.
        </AlertDescription>
      </Alert>

      <form
        noValidate
        onSubmit={form.handleSubmit(handleSubmit, handleError)}
        className="grid grid-cols-1 items-start gap-4 lg:grid-cols-2 xl:grid-cols-3"
      >
        {lockedByUsdot ? (
          <LockedField
            label="Legal Business Name"
            value={lockedDisplayValues?.legalName}
          />
        ) : (
          <FormField
            control={form.control}
            name="legalName"
            render={({ field }) => (
              <InputBox
                label="Legal Business Name"
                disabled={isSubmitting}
                required
                {...field}
              />
            )}
          />
        )}

        {lockedByUsdot ? (
          <LockedField
            label="Doing Business As (DBA) Name"
            value={lockedDisplayValues?.dbaName}
          />
        ) : (
          <FormField
            control={form.control}
            name="dbaName"
            render={({ field }) => (
              <InputBox
                label="Doing Business As (DBA) Name"
                disabled={isSubmitting}
                {...field}
                value={field.value ?? ''}
              />
            )}
          />
        )}

        {withBusinessType && (
          <FormField
            control={form.control}
            name="businessType"
            render={({ field }) => (
              <Field label="Business Entity Type" required>
                <Select
                  onValueChange={field.onChange}
                  value={field.value}
                  required
                >
                  <SelectTrigger
                    className="bg-background w-full"
                    aria-label="Business Entity Type"
                  >
                    <SelectValue placeholder="Select business type" />
                  </SelectTrigger>

                  <SelectContent>
                    {Object.values(BusinessType).map((type) => (
                      <SelectItem key={type} value={type}>
                        {format.Enum(BusinessType, type)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            )}
          />
        )}

        {withStateFileNumber && (
          <FormField
            control={form.control}
            name="stateFileNumber"
            render={({ field }) => (
              <InputBox
                name={field.name}
                onBlur={field.onBlur}
                type="text"
                label="Texas Secretary of State File Number (if applicable)"
                disabled={isSubmitting}
                value={field.value ?? ''}
                maxLength={10}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const n = e.target.value.replace(/\D/g, '')
                  field.onChange(n === '' ? null : Number(n))
                }}
              />
            )}
          />
        )}

        {lockedByUsdot && (
          <LockedField label="USDOT" value={lockedDisplayValues?.usdotNumber} />
        )}

        {withTaxId && (
          <FormField
            control={form.control}
            name="taxId"
            render={({ field }) => (
              <InputBox
                name={field.name}
                onBlur={field.onBlur}
                type="text"
                label="EIN"
                disabled={isSubmitting}
                value={field.value}
                maxLength={9}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  field.onChange(e.target.value.replace(/\D/g, ''))
                }}
                required
              />
            )}
          />
        )}

        {withOwnerInfo && (
          <>
            <Header variant="h4" className="mt-4 lg:col-span-2 xl:col-span-3">
              Owner Information
            </Header>

            <FormField
              control={form.control}
              name="ownerInfo.name"
              render={({ field }) => (
                <InputBox
                  label="Full Name"
                  maxLength={100}
                  disabled={isSubmitting}
                  required
                  {...field}
                />
              )}
            />

            <FormField
              control={form.control}
              name="ownerInfo.email"
              render={({ field }) => (
                <InputBox
                  label="Email"
                  maxLength={255}
                  disabled={isSubmitting}
                  required
                  {...field}
                />
              )}
            />

            <FormField
              control={form.control}
              name="ownerInfo.phoneNumber"
              render={({ field }) => (
                <InputBox
                  label="Phone Number"
                  maxLength={14}
                  disabled={isSubmitting}
                  required
                  {...field}
                  value={field.value
                    .replace(/\D/g, '') // strip non-numeric characters
                    .replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')} // auto-format as (123) 456-7890
                />
              )}
            />
          </>
        )}

        {lockedByUsdot ? (
          <>
            <Separator className="my-2 lg:col-span-2 xl:col-span-3" />
            <LockedField
              label="Physical Address"
              value={lockedDisplayValues?.physicalAddress}
            />
            <LockedField
              label="Mailing Address"
              value={lockedDisplayValues?.mailingAddress}
            />
          </>
        ) : (
          <>
            <Header variant="h4" className="mt-4 lg:col-span-2 xl:col-span-3">
              Physical Address
            </Header>

            <FormField
              control={form.control}
              name="physicalAddress.street1"
              render={({ field }) => (
                <InputBox
                  label="Street Address"
                  maxLength={255}
                  disabled={isSubmitting}
                  required
                  {...field}
                />
              )}
            />

            <FormField
              control={form.control}
              name="physicalAddress.street2"
              render={({ field }) => (
                <InputBox
                  label="Street Address - Line 2"
                  maxLength={255}
                  disabled={isSubmitting}
                  {...field}
                />
              )}
            />

            <FormField
              control={form.control}
              name="physicalAddress.city"
              render={({ field }) => (
                <InputBox
                  label="City"
                  maxLength={100}
                  disabled={isSubmitting}
                  required
                  {...field}
                />
              )}
            />

            <FormField
              control={form.control}
              name="physicalAddress.stateProvince"
              render={({ field }) => (
                <Field label="State" required>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                    disabled={isSubmitting}
                    required
                  >
                    <SelectTrigger
                      className="bg-background w-full"
                      aria-label="State"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectLabel>United States</SelectLabel>
                        {states.map((state) => (
                          <SelectItem key={state.code} value={state.code}>
                            {state.name}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                      <SelectGroup>
                        <SelectLabel>Canada</SelectLabel>
                        {provinces.map((province) => (
                          <SelectItem key={province.code} value={province.code}>
                            {province.name}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </Field>
              )}
            />

            <FormField
              control={form.control}
              name="physicalAddress.postalCode"
              render={({ field }) => (
                <InputBox
                  label={
                    isUSA(form.watch('physicalAddress.stateProvince'))
                      ? 'Zip Code'
                      : 'Postal Code'
                  }
                  maxLength={
                    isUSA(form.watch('physicalAddress.stateProvince')) ? 10 : 7
                  }
                  disabled={isSubmitting}
                  required
                  {...field}
                  value={field.value.toUpperCase()}
                />
              )}
            />

            <Header variant="h4" className="mt-4 lg:col-span-2 xl:col-span-3">
              Mailing Address
            </Header>

            {!mailingIsBusiness && (
              <>
                <FormField
                  control={form.control}
                  name="mailingAddress.street1"
                  render={({ field }) => (
                    <InputBox
                      label="Street Address"
                      maxLength={255}
                      disabled={isSubmitting}
                      required
                      {...field}
                    />
                  )}
                />

                <FormField
                  control={form.control}
                  name="mailingAddress.street2"
                  render={({ field }) => (
                    <InputBox
                      label="Street Address - Line 2"
                      maxLength={255}
                      disabled={isSubmitting}
                      {...field}
                    />
                  )}
                />

                <FormField
                  control={form.control}
                  name="mailingAddress.city"
                  render={({ field }) => (
                    <InputBox
                      label="City"
                      maxLength={50}
                      disabled={isSubmitting}
                      required
                      {...field}
                    />
                  )}
                />

                <FormField
                  control={form.control}
                  name="mailingAddress.stateProvince"
                  render={({ field }) => (
                    <Field label="State" required>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                        disabled={isSubmitting}
                        required
                      >
                        <SelectTrigger
                          className="bg-background w-full"
                          aria-label="State"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            <SelectLabel>United States</SelectLabel>
                            {states.map((state) => (
                              <SelectItem key={state.code} value={state.code}>
                                {state.name}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                          <SelectGroup>
                            <SelectLabel>Canada</SelectLabel>
                            {provinces.map((province) => (
                              <SelectItem
                                key={province.code}
                                value={province.code}
                              >
                                {province.name}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                    </Field>
                  )}
                />

                <FormField
                  control={form.control}
                  name="mailingAddress.postalCode"
                  render={({ field }) => (
                    <InputBox
                      label={
                        isUSA(form.watch('mailingAddress.stateProvince'))
                          ? 'Zip Code'
                          : 'Postal Code'
                      }
                      maxLength={
                        isUSA(form.watch('mailingAddress.stateProvince'))
                          ? 10
                          : 7
                      }
                      disabled={isSubmitting}
                      required
                      {...field}
                      value={field.value.toUpperCase()}
                    />
                  )}
                />
              </>
            )}

            <FormField
              control={form.control}
              name="mailingIsBusiness"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-2 lg:col-span-2 xl:col-span-3">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      disabled={isSubmitting}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel className="text-sm">
                      Same as physical address.
                    </FormLabel>
                    <FormMessage />
                  </div>
                </FormItem>
              )}
            />
          </>
        )}

        {/* Submit button(s) */}
        {children({ isSubmitting })}
      </form>
      {addressValidationDialog}
    </Form>
  )
}

export const BusinessEntityFormErrorId = 'error:business-entity-form'

interface FieldProps {
  label: string
  required?: boolean
}

export function Field({
  label,
  required = false,
  children,
}: PropsWithChildren<FieldProps>) {
  return (
    <FormItem>
      {required ? (
        <FormLabel className="gap-1">
          {label}
          <span className="text-destructive">*</span>
        </FormLabel>
      ) : (
        <FormLabel>{label}</FormLabel>
      )}
      <FormControl>{children}</FormControl>
      <FormMessage />
    </FormItem>
  )
}

export type TFormSchema = z.infer<typeof FormSchema>
const FormSchema = z
  .object({
    legalName: z.string().min(1, 'Required'),
    dbaName: z.string().nullable(),
    businessType: z.enum(BusinessType, { error: 'Required' }).optional(),
    stateFileNumber: z.number().optional().nullable(),
    taxId: z.string().min(1, 'Required').optional(),
    physicalAddress: addressSchema,
    mailingIsBusiness: z.boolean(),
    mailingAddress: addressSchema,
    ownerInfo: z
      .object({
        name: z.string().min(1, 'Required'),
        phoneNumber: phoneSchema.min(1, 'Required'),
        email: emailSchema.min(1, 'Required'),
      })
      .optional(),

    // field flags, not used in output but used in refined validations
    withBusinessType: z.boolean().optional(),
    withTaxId: z.boolean().optional(),
  })
  .superRefine(({ businessType, taxId, withTaxId, withBusinessType }, ctx) => {
    // Business type required
    if (withBusinessType && !businessType) {
      ctx.addIssue({
        path: ['businessType'],
        message: 'Required',
        code: 'custom',
      })
    }

    // Tax ID is required
    if (withTaxId) {
      // Required
      if (!taxId?.trim().length) {
        ctx.addIssue({
          path: ['taxId'],
          message: 'Required',
          code: 'custom',
        })
      }

      // Must be exactly 9 digits
      if (taxId?.trim().length !== 9) {
        ctx.addIssue({
          path: ['taxId'],
          message: 'Must be exactly 9 digits',
          code: 'custom',
        })
      }
    }
  })
